import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import {
  CART_STATE_ATTRIBUTE,
  hydrateSelectedStateFromCart,
  markHasSelectedState,
  refreshCartItemSections,
  SELECTED_STATE_STORAGE_KEY,
  syncSelectedStateToCart,
} from '@theme/beaumont-cart-state';
import { decorateCollectionLinks, initStatePlpFilter, syncStatePlpFilter } from '@theme/state-plp-filter';

const STATE_CHANGE_EVENT = 'state-selector:change';
const LABEL_SELECTOR = '[data-state-selector-label]';
const SELECTION_CLOSE_DELAY_MS = 500;

/**
 * @param {string} text
 */
function updateAllStateLabels(text) {
  document.querySelectorAll(LABEL_SELECTOR).forEach((element) => {
    element.textContent = text;
  });
}

/**
 * @param {string} code
 * @returns {string}
 */
function getStateNameForCode(code) {
  const stateSelector = document.querySelector('state-selector-component');
  const radio = stateSelector?.querySelector(
    `input[type="radio"][name="au-state"][value="${CSS.escape(code)}"]`
  );

  if (radio instanceof HTMLInputElement) {
    return radio.dataset.stateName || code;
  }

  return code;
}

/**
 * @param {string} code
 */
function syncSelectedRadio(code) {
  const stateSelector = document.querySelector('state-selector-component');
  const radio = stateSelector?.querySelector(
    `input[type="radio"][name="au-state"][value="${CSS.escape(code)}"]`
  );

  if (radio instanceof HTMLInputElement) {
    radio.checked = true;
  }
}

/**
 * @param {string} code
 * @param {string} name
 */
async function publishStateChange(code, name) {
  sessionStorage.setItem(SELECTED_STATE_STORAGE_KEY, code);
  markHasSelectedState();
  updateAllStateLabels(name);
  syncSelectedRadio(code);

  try {
    await syncSelectedStateToCart(code);
    await refreshCartItemSections();
  } catch (error) {
    console.error('Failed to sync selected state to cart attribute', error);
  }

  document.dispatchEvent(
    new CustomEvent(STATE_CHANGE_EVENT, {
      bubbles: true,
      detail: { code, name },
    })
  );
}

/**
 * Restores the selected state radio and syncs header / drawer labels from session storage.
 */
export function syncStateSelectorLabelsFromStorage() {
  const saved = sessionStorage.getItem(SELECTED_STATE_STORAGE_KEY);

  if (!saved) return;

  markHasSelectedState();
  syncSelectedRadio(saved);
  updateAllStateLabels(getStateNameForCode(saved));
}

/**
 * Re-sync labels after deferred header hydration injects the mobile drawer button.
 */
function observeHeaderHydration() {
  const component = document.querySelector('state-selector-component');
  const section = component?.closest('[id^="shopify-section-"]');

  if (!section) return;

  if (section.dataset.hydrated === 'true') {
    syncStateSelectorLabelsFromStorage();
    return;
  }

  const observer = new MutationObserver(() => {
    if (section.dataset.hydrated !== 'true') return;

    syncStateSelectorLabelsFromStorage();
    observer.disconnect();
  });

  observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
}

/**
 * Aligns UI labels with cart attribute + session storage on first load.
 */
async function hydrateStateFromCart() {
  try {
    const result = await hydrateSelectedStateFromCart();

    if (!result?.code) return;

    markHasSelectedState();
    syncSelectedRadio(result.code);
    updateAllStateLabels(getStateNameForCode(result.code));

    decorateCollectionLinks(result.code);

    // Cart may win over a stale session value — re-project onto PLP/search URLs.
    await syncStatePlpFilter({ historyMode: 'replace', stateCode: result.code, reason: 'hydrate' });

    // Session → cart writes must remorph checkout buttons (no-state disables them).
    if (result.didSync) {
      await refreshCartItemSections();
    }
  } catch (error) {
    console.error('Failed to hydrate selected state from cart', error);
  }
}

/**
 * @returns {boolean}
 */
function isMenuDrawerOpen() {
  const headerDrawer = document.querySelector('header-drawer');

  return Boolean(headerDrawer?.isOpen);
}

/**
 * Australian state selector drawer for the header.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLElement} triggerLabel – The trigger button label.
 *
 * @extends {DialogComponent}
 */
class StateSelectorComponent extends DialogComponent {
  /** @type {string} */
  #defaultLabel = '';

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  #openedOverMenu = false;

  connectedCallback() {
    super.connectedCallback();
    this.#defaultLabel =
      this.refs.triggerLabel?.dataset.stateSelectorDefault?.trim() ||
      this.refs.triggerLabel?.textContent?.trim() ||
      '';
    initStatePlpFilter();
    hydrateStateFromCart();
    syncStateSelectorLabelsFromStorage();
    observeHeaderHydration();
    this.#bindRadios();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    syncStateSelectorLabelsFromStorage();
  }

  #handleCartUpdate = () => {
    syncStateSelectorLabelsFromStorage();
  };

  #bindRadios() {
    const radios = this.querySelectorAll('input[type="radio"][name="au-state"]');

    radios.forEach((radio) => {
      radio.addEventListener('change', this.#handleStateChange);
    });
  }

  /**
   * @param {Event} event
   */
  #handleStateChange = async (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    const name = input.dataset.stateName || input.value;

    publishStateChange(input.value, name);
    await new Promise((resolve) => setTimeout(resolve, SELECTION_CLOSE_DELAY_MS));
    this.closeDialog();
  };

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#openedOverMenu = isMenuDrawerOpen();

    if (!this.#openedOverMenu) {
      this.#previousScrollY = window.scrollY;
    }

    requestAnimationFrame(() => {
      if (!this.#openedOverMenu) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#previousScrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!this.#openedOverMenu) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#openedOverMenu = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };
}

if (!customElements.get('state-selector-component')) {
  customElements.define('state-selector-component', StateSelectorComponent);
}

export { CART_STATE_ATTRIBUTE, SELECTED_STATE_STORAGE_KEY };
